// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import checkoutSharedStyles from "@conklin/App/SharedStyles/CheckoutStyles";
import Tooltip from "@conklin/MobiusOverrides/Tooltip";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@conklin/Overrides/Components/AddressInfoDisplay";
import IconNotificationTooltip from "@conklin/XDL/Icons/IconNotificationTooltip";
import { parseBoolean, Spacer } from "@conklin/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getQuoteState } from "@insite/client-framework/Store/Data/Quotes/QuotesSelector";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Icon from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";

const mapStateToProps = (state: ApplicationState) => ({
    quoteState: getQuoteState(state, state.pages.rfqConfirmation.quoteId),
});

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

export interface RfqConfirmationAddressStyles {
    wrapper?: InjectableCss;
    title?: TypographyPresentationProps;
    heading?: TypographyPresentationProps;
    addressInfoDisplay?: AddressInfoDisplayStyles;
}

export const rfqConfirmationAddressStyles: RfqConfirmationAddressStyles = {};

const styles = rfqConfirmationAddressStyles;

const RfqConfirmationAddress: FC<Props> = ({ quoteState }) => {
    if (!quoteState.value || !quoteState.value.shipTo) {
        return null;
    }

    const shipTo = quoteState.value.shipTo;

    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Typography {...styles.heading}>{translate("Addresses")}</Typography>
                {shipTo && (
                    <StyledWrapper>
                        <Typography {...styles.title}>{translate("Shipping Address")}</Typography>
                        <AddressInfoDisplay
                            firstName={shipTo.firstName}
                            lastName={shipTo.lastName}
                            companyName={shipTo.companyName}
                            attention={shipTo.attention}
                            address1={shipTo.address1}
                            address2={shipTo.address2}
                            city={shipTo.city}
                            state={shipTo.state ? shipTo.state.abbreviation : undefined}
                            postalCode={shipTo.postalCode}
                            country={shipTo.country ? shipTo.country.abbreviation : undefined}
                            phone={shipTo.phone}
                            fax={shipTo.fax}
                            extendedStyles={styles.addressInfoDisplay}
                        />
                    </StyledWrapper>
                )}
            </StyledWrapper>
            {parseBoolean(quoteState.value.properties.taxExempt) && (
                <StyledWrapper {...styles.wrapper}>
                    <Typography {...styles.heading}>{translate("Purchase Details")}</Typography>

                    <StyledWrapper>
                        <Typography {...checkoutSharedStyles.sectionTitle}>{translate("Tax Exemption")}</Typography>
                        <Typography {...checkoutSharedStyles.sectionValue}>
                            {translate("Tax Exempt Status Is Pending")}
                            <Tooltip
                                text={siteMessage("Rfq_RquestQuoteForm_TooltipMessage")}
                                triggerComponent={<Icon size={16} src={IconNotificationTooltip} />}
                            />
                        </Typography>
                    </StyledWrapper>
                    <Spacer space={24} />
                    {!!quoteState.value.properties.taxExemptCertificateNumber && (
                        <StyledWrapper>
                            <Typography {...checkoutSharedStyles.sectionTitle}>
                                {translate("Tax Exempt Certificate Number")}
                            </Typography>
                            <Typography {...checkoutSharedStyles.sectionValue}>
                                {quoteState.value.properties.taxExemptCertificateNumber}
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(RfqConfirmationAddress),
    definition: {
        displayName: "RFQ Quote Confirmation Addresses",
        allowedContexts: ["RfqConfirmationPage"],
        fieldDefinitions: [],
        group: "RFQ Quote Confirmation",
    },
};

export default widgetModule;
